import { readCookieHeader } from './cookies';
import { IMPERSONATING_COOKIE, cookieName } from './authCookieNames';
import { randomId } from 'elowen-plugin-shared/randomId';

// The daemon token lives in an httpOnly cookie the browser JS cannot read. Browser code coordinates only
// lifecycle signals: authentication cleared, or an account identity transition beginning/committing.
export const AUTH_CLEARED_EVENT = 'elowen:auth-cleared';
export const AUTH_TRANSITION_EVENT = 'elowen:auth-transition';
const AUTH_TRANSITION_STORAGE_KEY = 'elowen:auth-transition';

// `ready` is local session proof from LoginGate, never a cross-tab mutation outcome.
type AuthTransitionPhase = 'start' | 'commit' | 'rollback' | 'ready';
export interface AuthTransition {
  id: string;
  phase: AuthTransitionPhase;
  /** Same account, rotated credential: keep mounted forms and the current route. */
  refresh?: true;
  /** Where a committed switch lands: a path in this app. The shell goes to `/dash` without one. */
  landing?: string;
}

/** A landing is a path in this app, never an address that leaves it. Returns null for anything else.
 *
 *  The same-origin check is not enough on its own, because the parser NORMALISES before it answers:
 *  `/.//evil.com`, `/..//evil.com`, `/%2e//evil.com` and `/a/..//evil.com` all resolve against this origin
 *  and come back as the path `//evil.com`, which a router then follows as a protocol-relative URL onto
 *  another site. So the normalised result is checked as well, not only the input. A backslash is refused
 *  outright: the parser rewrites it to a separator for special schemes, so no input containing one means
 *  what it reads as. */
function appPath(href: string): string | null {
  if (!href.startsWith('/') || href.startsWith('//') || href.includes('\\')) return null;
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  return path.startsWith('//') ? null : path;
}

let activeTransition: string | null = null;
// A delayed sibling start can replace the observed transition, never this document's pending write.
let ownedTransition: Pick<AuthTransition, 'id' | 'refresh'> | null = null;


/** Publish locally and to sibling tabs. The storage value contains no identity or credential, and is
 *  removed immediately; its only purpose is making the browser emit a cross-tab `storage` event. */
function publishTransition(detail: AuthTransition): void {
  beginAuthGeneration();
  window.dispatchEvent(new CustomEvent<AuthTransition>(AUTH_TRANSITION_EVENT, { detail }));
  try {
    localStorage.setItem(AUTH_TRANSITION_STORAGE_KEY, JSON.stringify({ ...detail, nonce: Math.random() }));
    localStorage.removeItem(AUTH_TRANSITION_STORAGE_KEY);
  } catch { /* storage unavailable: the initiating tab still transitions safely */ }
}

// One cross-tab bridge per document, including before an auth gate subscribes.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (ownedTransition) finishAuthTransition(ownedTransition.id, 'rollback', ownedTransition);
  });
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== AUTH_TRANSITION_STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as Partial<AuthTransition>;
      if (typeof parsed.id === 'string' && ['start', 'commit', 'rollback'].includes(String(parsed.phase))) {
        const landing = typeof parsed.landing === 'string' ? appPath(parsed.landing) : null;
        if (parsed.phase === 'start') activeTransition = parsed.id;
        else if (activeTransition === parsed.id) activeTransition = null;
        beginAuthGeneration();
        window.dispatchEvent(new CustomEvent<AuthTransition>(AUTH_TRANSITION_EVENT, {
          detail: { id: parsed.id, phase: parsed.phase as AuthTransitionPhase, ...(landing ? { landing } : {}), ...(parsed.refresh === true ? { refresh: true } : {}) },
        }));
      }
    } catch { /* malformed cross-tab state is ignored */ }
  });
}

export function subscribeAuthTransitions(listener: (transition: AuthTransition) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onLocal = (event: Event) => listener((event as CustomEvent<AuthTransition>).detail);
  window.addEventListener(AUTH_TRANSITION_EVENT, onLocal);
  return () => { window.removeEventListener(AUTH_TRANSITION_EVENT, onLocal); };
}

/** Fence all tabs before an identity switch or same-account credential refresh starts. */
export function beginAuthTransition(refresh = false): string {
  if (activeTransition) throw new Error('identity transition already in progress');
  activeTransition = randomId();
  ownedTransition = { id: activeTransition, ...(refresh ? { refresh: true } : {}) };
  publishTransition({ ...ownedTransition, phase: 'start' });
  return activeTransition;
}

/** End the same transition; refresh consumers revalidate without unmounting their live form. */
export function finishAuthTransition(id: string, phase: 'commit' | 'rollback', options: Pick<AuthTransition, 'landing' | 'refresh'> = {}): void {
  if (ownedTransition?.id !== id) return; // A pagehide rollback already ended a late request continuation.
  if (activeTransition === id) activeTransition = null;
  ownedTransition = null;
  publishTransition({ id, phase, ...(phase === 'commit' && options.landing ? { landing: options.landing } : {}), ...(options.refresh ? { refresh: true } : {}) });
}

/** Which session this document is on. It advances when a login begins to establish a NEW one, which is
 *  what tells two otherwise identical 401s apart: one answering a request issued in an earlier generation
 *  describes the session that is already gone, not the one that exists now. The pre-gate config read (SkinProvider,
 *  ConfiguredToastProvider, mounted above the auth gate) is the request that makes this concrete — it is
 *  issued logged out and 401s, and answering it with clearToken() deleted the session the user had just
 *  created, dropping them back on the login form. */
let generation = 0;

export function authGeneration(): number {
  return generation;
}

/** A login is about to establish a new session. Marked BEFORE the request goes out: the new cookie exists
 *  from the moment the daemon answers, not from the moment this client observes it, so a 401 that lands in
 *  between must not be acted on either. */
export function beginAuthGeneration(): void {
  generation += 1;
}

/** End the session and notify the local auth gate. */
export function clearToken(): void {
  if (typeof window === 'undefined') return;
  activeTransition = null;
  ownedTransition = null;
  beginAuthGeneration();
  void fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => { /* network/daemon down: still signal the UI */ });
  window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
}

/** Close the local gate only for a current-generation 401 outside an active credential/identity
 *  transition. No cookie write: this request cannot know whether another tab has since replaced it.
 *  A committed transition's fresh /auth/me read remains the authority for opening the gate again. */
export function clearTokenIfCurrent(issuedIn: number): void {
  if (issuedIn !== generation || activeTransition !== null || typeof window === 'undefined') return;
  // A 401 already proves this request's token invalid. It cannot safely expire the cookie shared
  // by other tabs, which may have rotated it since this request began. Explicit logout owns that.
  beginAuthGeneration();
  window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
}

/** The display name of the user currently being impersonated, or null when not impersonating. */
export function impersonatingAs(): string | null {
  if (typeof document === 'undefined') return null;
  const name = cookieName(IMPERSONATING_COOKIE, window.location.protocol === 'https:');
  return readCookieHeader(document.cookie, name);
}

/** Swap to another account. The start signal synchronously tears down old account state before the BFF
 *  changes the cookie; commit then loads the new identity without a document reload. A refusal rejects
 *  with the reason the daemon gave, carried on the Error's message ("forbidden"); a surface that catches it
 *  decides what to show. The status line is only the fallback for a refusal body that carries no `error`
 *  string. `landing` is the app path the shell opens as the new account; it is refused before anything
 *  starts when it is not a path in this app. */
export async function impersonateUser(userId: number, landing?: string): Promise<void> {
  const target = landing === undefined ? undefined : appPath(landing);
  if (target === null) throw new Error('landing must be a path in this app');
  const id = beginAuthTransition();
  try {
    const res = await fetch('/api/auth/impersonate', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'content-type': 'application/json' }, body: JSON.stringify({ userId }),
    });
    if (!res.ok) {
      // The BFF forwards the daemon's body verbatim. Reading the `error` string HERE, instead of importing
      // `ElowenApiError`, is what keeps this module a leaf: `elowenClient` imports it back, so that import
      // would be a cycle — and the caller gets the same string `apiErrorMessage` would have read off it.
      let body: { error?: unknown } | undefined;
      try { body = (await res.json()) as { error?: unknown }; } catch { /* non-JSON refusal body */ }
      throw new Error(typeof body?.error === 'string' ? body.error : `impersonate failed: ${res.status}`);
    }
    finishAuthTransition(id, 'commit', { landing: target });
  } catch (error) {
    finishAuthTransition(id, 'rollback');
    throw error;
  }
}

/** A lost response is the one retryable failure here: the daemon keeps the exchange idempotent for a
 *  bounded window, so repeating the exact cookie-bound request cannot mint a second authority chain. */
async function requestStopImpersonation(): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let res: Response;
    try {
      res = await fetch('/api/auth/stop-impersonate', { method: 'POST', credentials: 'same-origin' });
    } catch (error) {
      if (attempt > 0) throw error;
      lastError = error;
      continue;
    }
    if (res.ok) return;
    const error = new Error(`stop impersonation failed: ${res.status}`);
    if (res.status < 500 || attempt > 0) throw error;
    lastError = error;
  }
  throw lastError instanceof Error ? lastError : new Error('stop impersonation failed');
}

/** Exchange the active target session and opaque return proof for a fresh admin session. */
export async function stopImpersonation(): Promise<void> {
  const id = beginAuthTransition();
  try {
    await requestStopImpersonation();
    finishAuthTransition(id, 'commit');
  } catch (error) {
    finishAuthTransition(id, 'rollback');
    throw error;
  }
}
